import { Button, Icon, Tooltip } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { useMutation } from '@/hooks/api';
import { useRolloutWindows } from '@/hooks/rollout_windows';
import { useCanWriteById } from '@/hooks/workspace';
import { rolloutWindowsTableColumns } from '../common/table/column_definitions/RolloutWindows';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { AddRolloutWindowDialog } from './AddRolloutWindowDialog';

type Props = {
  deviceId: number;
  workspaceId: number;
};

const rolloutWindowsDescription =
  'Schedule rollout windows to control when deployments update on your device, enabling coordinated releases with minimal downtime. Uses cron expressions for specifying iterations.';

export const DeviceRolloutWindows = ({ deviceId, workspaceId }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canWrite = useCanWriteById(workspaceId);

  const { data, isLoading, isValidating, mutate } = useRolloutWindows(workspaceId, deviceId);

  const deleteRolloutWindow = useMutation<
    void,
    { deviceId: number; workspaceId: number; rolloutWindowId: number }
  >(
    'device-delete-rollout-window',
    async (client, { deviceId, workspaceId, rolloutWindowId }) =>
      await client.v1.workspaces[':workspaceId'].devices[':deviceId']['rollout-windows'][
        ':rolloutWindowId'
      ].$delete({
        param: {
          workspaceId: workspaceId.toString(),
          deviceId: deviceId.toString(),
          rolloutWindowId: rolloutWindowId.toString(),
        },
      })
  );

  const handleDeleteRolloutWindow = async (rolloutWindowId: number) => {
    try {
      await deleteRolloutWindow({
        deviceId,
        rolloutWindowId,
        workspaceId,
      });
      void mutate();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    }
  };

  return (
    <>
      <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="DeviceRolloutWindows">
        <Table
          addButton={
            canWrite
              ? () => (
                  <Tooltip label="Add a new rollout window" placement="left">
                    <Button
                      borderRightRadius="0px"
                      leftIcon={<Icon as={PlusBold} />}
                      onClick={() => setIsDialogOpen(true)}
                      size="sm"
                      variant="outline"
                    >
                      Add
                    </Button>
                  </Tooltip>
                )
              : undefined
          }
          columns={rolloutWindowsTableColumns(canWrite, handleDeleteRolloutWindow)}
          count={data?.itemCount || 0}
          data={data?.rolloutWindows || []}
          description={rolloutWindowsDescription}
          isLoading={Boolean(isLoading || !data)}
          isValidating={isValidating}
          refresh={mutate}
          title="Rollout Windows"
        />
      </TableStoreProvider>
      {canWrite && (
        <AddRolloutWindowDialog
          deviceId={deviceId}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSuccess={mutate}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};
